<script>
/* global axios */
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

// v188 — além de foto+nome, a aba configura:
//  - Links no topo da página (Site/Instagram/Facebook), cada um com ativo+URL;
//  - Instalação PWA (tela "Instale o app…"), com mensagem/botões por plataforma
//    e quando exibir (ao iniciar OU botão "Ler Notificação!" no chat).
// TUDO nasce DESLIGADO (on:false / enabled:false) — o admin liga aqui depois.
const defaultLinks = () => ({
  site: { on: false, url: '' },
  instagram: { on: false, url: '' },
  facebook: { on: false, url: '' },
});
// Todos os textos/peças da tela de entrada (o que o cliente lê antes de
// conversar). Vazio = o webchat usa o padrão dele, então não gravamos default
// aqui: se o padrão mudar no código, quem nunca editou o campo acompanha.
// Temas do webchat. As cores aqui são as MESMAS do web/index.html
// (html[data-tema="..."]) — a prévia mostra o tema de verdade, não uma
// aproximação. Mexeu lá, mexa aqui.
const TEMAS_WEBCHAT = [
  { id: '', nome: 'Automático', desc: 'Segue o aparelho do cliente',
    fundo: '#8d99a6', chat: '#cfd6dd', painel: '#eef1f4', balao: '#ffffff', meu: '#d9fdd3', texto: '#111b21' },
  { id: 'claro', nome: 'Claro', desc: 'O claro do WhatsApp',
    fundo: '#d9d2c9', chat: '#efeae2', painel: '#f0f2f5', balao: '#ffffff', meu: '#d9fdd3', texto: '#111b21' },
  { id: 'escuro', nome: 'Escuro', desc: 'O escuro do WhatsApp',
    fundo: '#0b141a', chat: '#0b141a', painel: '#111b21', balao: '#202c33', meu: '#005c4b', texto: '#e9edef' },
  { id: 'meianoite', nome: 'Meia-noite', desc: 'Azul profundo',
    fundo: '#081120', chat: '#0a1628', painel: '#0f1e33', balao: '#1a2f4a', meu: '#134e4a', texto: '#e8eef7' },
  { id: 'grafite', nome: 'Grafite', desc: 'Cinza neutro',
    fundo: '#111214', chat: '#17181a', painel: '#1e1f22', balao: '#2b2d31', meu: '#3a3d43', texto: '#e6e7e9' },
  { id: 'areia', nome: 'Areia', desc: 'Claro quente',
    fundo: '#e6dccd', chat: '#f3ece1', painel: '#fbf7f1', balao: '#ffffff', meu: '#e7f5d8', texto: '#2b2419' },
];
const defaultStart = () => ({
  title: '', subtitle: '', consent: '',
  field_label: '', field_hint: '', placeholder: '', robot: '', button: '',
  badge_on: false, badge_title: '', badge_value: '', badge_text: '',
  badge_color: '', footer_on: false,
});
const defaultSoftAsk = () => ({
  title: 'Quer ser avisado quando ficar pronto?',
  text: 'Ativo um aviso no seu celular assim que eu tiver a resposta — mesmo com esta aba fechada.',
  btn_yes: 'Ativar avisos',
  btn_no: 'Agora não',
  snooze_days: 3,
});
const defaultChromeRedirect = () => ({
  on: false,
  msg: 'Neste navegador o aviso não funciona direito. Abro sua conversa no Chrome?',
  btn: 'Abrir no Chrome',
});
const defaultPush = () => ({
  enabled: false,
  prompt_when: 'after_first_reply',
  every_message: false,
  soft_ask: defaultSoftAsk(),
  chrome_redirect: defaultChromeRedirect(),
  labels: {},
});
// Mensagens sugeridas ao ligar as etiquetas principais do funil
const PUSH_SUGGESTIONS = {
  fgts_simulacao: 'Sua oferta de crédito está pronta! Toque para ver.',
  fgts_link: 'Seu link de formalização chegou! Toque para assinar.',
  fgts_pago: 'Seu contrato foi pago! ✅',
  clt_simulacao: 'Sua simulação do consignado CLT ficou pronta! Toque para ver.',
  clt_link: 'Seu link de formalização chegou! Toque para assinar.',
  clt_pago: 'Seu contrato foi pago! ✅',
  clt_retorno_agendado: 'Voltamos a falar do seu crédito — toque para continuar.',
};
// `msg` nasce VAZIA de proposito: vazia, o webchat monta "Instale o app da
// <nome da caixa>" sozinho. Antes o default vinha com "Instale o app da Mazam"
// escrito, entao QUALQUER cliente que ativasse o PWA anunciava a Mazam no
// proprio dominio (visto na FOUR CRED). Quem quiser um texto proprio escreve.
const defaultPwa = () => ({
  enabled: false,
  when: 'start',
  android: {
    on: false,
    msg: '',
    btn_ok: 'Prosseguir',
    btn_alt: 'Continuar pelo Site',
  },
  ios: {
    on: false,
    msg: '',
    btn_ok: 'Prosseguir',
    btn_alt: 'Continuar pelo Site',
  },
});

// Template que pode sair SOZINHO no disparo automático: sem {{n}} no texto,
// sem cabeçalho de mídia e sem botão com parâmetro. Mesma regra do backend
// (MazamWppAutoTemplate.sem_variaveis?) — lá é a trava, aqui só filtra a lista.
const templateSemVariaveis = t =>
  !(t.components || []).some(c => {
    const tipo = String(c.type || '').toLowerCase();
    const formato = String(c.format || '').toLowerCase();
    const midia = tipo === 'header' && formato && formato !== 'text';
    const variavel = /\{\{[^}]*\}\}/;
    return (
      midia ||
      variavel.test(c.text || '') ||
      (c.buttons || []).some(b => variavel.test(b.url || '') || b.example)
    );
  });

export default {
  props: {
    inbox: { type: Object, required: true },
  },
  data() {
    return {
      name: '',
      photo: '',
      verified: false,
      // WhatsApp do atendimento: destino do gate "continuar no WhatsApp"
      // do roteiro. Fica FORA do modelo de site (configAtual) de propósito —
      // é dado operacional do cliente, não aparência reaproveitável.
      whatsapp: '',
      // META WPP: liga o botão de WhatsApp DENTRO da conversa (ao lado do MODO
      // IA) e diz para qual caixa de WhatsApp o cliente é levado.
      metaWpp: false,
      metaWppInboxId: '',
      // Template automático no WhatsApp ao entrar lead (super admin): caixa de
      // WhatsApp que envia + template no formato "nome|idioma".
      wppAutoAtivo: false,
      wppAutoInboxId: '',
      wppAutoTemplate: '',
      inboxAvatar: '',
      links: defaultLinks(),
      pwa: defaultPwa(),
      push: defaultPush(),
      domain: '',
      theme: { brand: '', preset: '', wallpaper: '' },
      brand: { logo: '', favicon: '', site_title: '' },
      start: defaultStart(),
      saving: false,
      stats: null,
      copied: false,
      // ---- modelos de site (espelho dos modelos de roteiro) ----
      templates: [],
      tplSel: '',
      tplPadrao: '',
      // Nome e foto são a IDENTIDADE do cliente, não a aparência: aplicar um
      // modelo não deve rebatizar a caixa sem que o operador peça.
      tplIdentidade: false,
    };
  },
  computed: {
    temas() {
      return TEMAS_WEBCHAT;
    },
    ...mapGetters({
      currentUser: 'getCurrentUser',
      caixasWhatsapp: 'inboxes/getWhatsAppInboxes',
    }),
    // Gerenciar modelos (salvar/definir padrão/excluir) é do super admin id 1,
    // como criar caixa SITE WEB e exportar/importar roteiro: o modelo padrão
    // vale para TODA caixa nova da instalação. Usar um modelo na caixa aberta
    // segue a regra da tela (admin da conta ou membro daquela caixa).
    ehSuperAdmin() {
      return this.currentUser?.id === 1;
    },
    wppAutoTemplates() {
      const caixa = this.caixasWhatsapp.find(
        c => String(c.id) === String(this.wppAutoInboxId)
      );
      return (caixa?.message_templates || []).filter(
        t => String(t.status).toLowerCase() === 'approved' && templateSemVariaveis(t)
      );
    },
    wppAutoTextoTemplate() {
      const t = this.wppAutoTemplates.find(
        x => `${x.name}|${x.language}` === this.wppAutoTemplate
      );
      const corpo = (t?.components || []).find(
        c => String(c.type).toLowerCase() === 'body'
      );
      return corpo?.text || '';
    },
    modeloPadrao() {
      return this.templates.find(t => t.padrao) || null;
    },
    accountId() {
      return this.$route.params.accountId;
    },
    // URL pública da caixa (Task B2): direto do additional_attributes que já
    // vem no objeto inbox do store (a index.json.jbuilder do canal API expõe
    // isso para admin OU membro — nenhuma chamada extra necessária). Sem
    // domínio configurado ainda, não renderiza o bloco.
    publicSiteUrl() {
      const domain = this.inbox?.additional_attributes?.mazam_web_domain;
      return domain ? `https://${domain}` : '';
    },
    vpsIp() {
      return '201.33.84.156';
    },
    cleanDomain() {
      return (this.domain || '')
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .replace(/^www\./, '');
    },
    previewSrc() {
      return this.photo || this.inboxAvatar || '';
    },
    // Todas as etiquetas da conta (para escolher quais disparam push)
    allLabels() {
      return this.$store.getters['labels/getLabels'] || [];
    },
  },
  watch: {
    allLabels() {
      this.ensureLabelRules();   // etiquetas chegam async do store
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');   // garante o store de etiquetas carregado
    this.load();
    this.loadStats();
    // A2 do review 14/08: modelos de site (mesmo a LEITURA) são exclusivos do
    // super admin id 1 — nem tenta carregar para quem não é, evitando um 403
    // previsível a cada abertura da aba.
    if (this.ehSuperAdmin) this.carregarTemplates();
  },
  methods: {
    // Endereço público copiável (Task B2): o dono/cliente da caixa usa isto
    // em campanhas. Clipboard API com fallback de textarea (Safari antigo /
    // contexto sem permissão) — melhor um copy manual do que nada.
    async copyPublicUrl() {
      if (!this.publicSiteUrl) return;
      try {
        await navigator.clipboard.writeText(this.publicSiteUrl);
      } catch (e) {
        const ta = document.createElement('textarea');
        ta.value = this.publicSiteUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    // Números do convite de notificação — sem eles, ajustar texto/momento é chute.
    async loadStats() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/stats`,
          { params: { inbox_id: this.inbox.id, days: 30 } }
        );
        this.stats = data.resumo || null;
      } catch (e) {
        this.stats = null;
      }
    },
    async load() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/config`,
          { params: { inbox_id: this.inbox.id } }
        );
        this.name = data.name || '';
        this.photo = data.photo || '';
        this.verified = data.verified === true;
        this.whatsapp = data.whatsapp || '';
        this.metaWpp = data.meta_wpp === true;
        this.metaWppInboxId = data.meta_wpp_inbox_id || '';
        const wppAuto = data.wpp_auto || {};
        this.wppAutoAtivo = wppAuto.ativo === true;
        this.wppAutoInboxId = wppAuto.inbox_id || '';
        this.wppAutoTemplate = wppAuto.template
          ? `${wppAuto.template}|${wppAuto.language || ''}`
          : '';
        this.inboxAvatar = data.inbox_avatar || '';
        // merge sobre os defaults (config antiga sem os campos novos = tudo OFF)
        this.links = Object.assign(defaultLinks(), data.links || {});
        const pwa = Object.assign(defaultPwa(), data.pwa || {});
        pwa.android = Object.assign(defaultPwa().android, (data.pwa || {}).android || {});
        pwa.ios = Object.assign(defaultPwa().ios, (data.pwa || {}).ios || {});
        this.pwa = pwa;
        const push = Object.assign(defaultPush(), data.push || {});
        push.labels = Object.assign({}, (data.push || {}).labels || {});
        push.soft_ask = Object.assign(defaultSoftAsk(), (data.push || {}).soft_ask || {});
        push.chrome_redirect = Object.assign(
          defaultChromeRedirect(), (data.push || {}).chrome_redirect || {}
        );
        this.push = push;
        this.domain = data.domain || '';
        this.theme = Object.assign({ brand: '', preset: '', wallpaper: '' }, data.theme || {});
        this.brand = Object.assign({ logo: '', favicon: '', site_title: '' }, data.brand || {});
        this.start = Object.assign(defaultStart(), data.start || {});
        this.ensureLabelRules();
      } catch (e) {
        // sem config ainda — começa vazio/desligado
      }
    },
    // Garante uma entrada {on,msg} para cada etiqueta da conta (reativo)
    ensureLabelRules() {
      (this.allLabels || []).forEach(l => {
        const t = l.title;
        if (!this.push.labels[t]) {
          this.push.labels[t] = { on: false, msg: PUSH_SUGGESTIONS[t] || '' };
        } else if (!this.push.labels[t].msg && PUSH_SUGGESTIONS[t]) {
          this.push.labels[t].msg = PUSH_SUGGESTIONS[t];
        }
      });
    },
    // 'photo' é a foto do topo do SITE e mora na raiz (this.photo, salva em
    // mazam_web_photo); 'logo'/'favicon' moram em this.brand. Antes tudo caía
    // em this.brand[field], então enviar arquivo em "Foto exibida no site"
    // gravava num this.brand.photo que ninguém lê nem envia — a foto
    // simplesmente não aparecia na conversa.
    pickImage(field, maxKB, e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > maxKB * 1024) {
        useAlert(`Imagem muito grande (máx. ${maxKB} KB).`);
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (field === 'photo') this.photo = reader.result;
        else this.brand[field] = reader.result;
      };
      reader.readAsDataURL(file);
    },
    clearImage(field) {
      if (field === 'photo') this.photo = '';
      else this.brand[field] = '';
    },
    // envia só as etiquetas marcadas (mantém o additional_attributes enxuto)
    pushPayload() {
      const labels = {};
      Object.keys(this.push.labels || {}).forEach(t => {
        const r = this.push.labels[t];
        if (r && r.on) labels[t] = { on: true, msg: (r.msg || '').trim() };
      });
      return {
        enabled: this.push.enabled,
        prompt_when: this.push.prompt_when,
        every_message: this.push.every_message,
        soft_ask: this.push.soft_ask,
        chrome_redirect: this.push.chrome_redirect,
        labels,
      };
    },
    // ---- modelos de site --------------------------------------------------
    // O modelo guarda a APARÊNCIA da caixa e nada mais: domínio, código,
    // roteiro e credenciais ficam de fora (o servidor descarta por allowlist).
    // Estes são exatamente os campos que o "Atualizar" já envia.
    configAtual() {
      return {
        verified: this.verified,
        theme: this.theme,
        brand: this.brand,
        start: this.start,
        links: this.links,
        pwa: this.pwa,
        push: this.pushPayload(),
        name: this.name,
        photo: this.photo,
      };
    },
    // Rede de segurança repetida: o grupo de botões some para quem não é id 1,
    // mas uma chamada pelo DevTools passaria por fora do botão escondido.
    // O servidor também trava (gerenciar_modelos_de_site!); isto é só a
    // mensagem clara na tela.
    bloqueadoParaGerenciarModelos() {
      if (this.ehSuperAdmin) return false;
      useAlert('Gerenciar modelos de site é restrito ao super admin.');
      return true;
    },
    async carregarTemplates() {
      // A2 do review 14/08: defesa em profundidade — mesmo chamado por fora
      // do mounted() (ex.: DevTools), não bate no endpoint se não for id 1.
      // O servidor também trava (gerenciar_modelos_de_site! no site_templates).
      if (!this.ehSuperAdmin) return;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/site_templates`,
          { params: { inbox_id: this.inbox.id } }
        );
        this.templates = data.templates || [];
        this.tplPadrao = data.padrao || '';
      } catch (e) {
        this.templates = [];
        this.tplPadrao = '';
      }
    },
    // Carrega o modelo NOS CAMPOS da tela e para por aí — igual ao "Usar
    // modelo" do roteiro. Quem salva de verdade continua sendo o botão
    // "Atualizar", depois da revisão do operador.
    async usarTemplate() {
      if (this.bloqueadoParaGerenciarModelos()) return;
      const t = this.templates.find(x => x.id === this.tplSel);
      if (!t) return;
      if (!window.confirm(
        `Aplicar o modelo "${t.nome}" SUBSTITUI a aparência que está na tela `
        + '(cores, logo, favicon, textos, links, PWA e push). Continuar?'
      )) return;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/site_templates`,
          { params: { id: this.tplSel, inbox_id: this.inbox.id } }
        );
        const c = data.config || {};
        if ('mazam_web_verified' in c) this.verified = c.mazam_web_verified === true;
        if (c.mazam_web_theme) this.theme = Object.assign({ brand: '', preset: '', wallpaper: '' }, c.mazam_web_theme);
        if (c.mazam_web_brand) {
          this.brand = Object.assign({ logo: '', favicon: '', site_title: '' }, c.mazam_web_brand);
        }
        if (c.mazam_web_start) {
          this.start = Object.assign(defaultStart(), c.mazam_web_start);
        }
        if (c.mazam_web_links) this.links = Object.assign(defaultLinks(), c.mazam_web_links);
        if (c.mazam_web_pwa) {
          const pwa = Object.assign(defaultPwa(), c.mazam_web_pwa);
          pwa.android = Object.assign(defaultPwa().android, c.mazam_web_pwa.android || {});
          pwa.ios = Object.assign(defaultPwa().ios, c.mazam_web_pwa.ios || {});
          this.pwa = pwa;
        }
        if (c.mazam_web_push) {
          const push = Object.assign(defaultPush(), c.mazam_web_push);
          // A5 do review 14/08: `push.labels` são TÍTULOS DE ETIQUETA da conta
          // de ORIGEM do modelo — importar todos colava regras de push para
          // etiquetas inexistentes na conta de destino (inertes, mas
          // persistidas). Mantém só as que existem nesta conta.
          const titulosDaConta = (this.allLabels || []).map(l => l.title);
          const labelsDoModelo = c.mazam_web_push.labels || {};
          push.labels = {};
          Object.keys(labelsDoModelo).forEach(titulo => {
            if (titulosDaConta.includes(titulo)) push.labels[titulo] = labelsDoModelo[titulo];
          });
          push.soft_ask = Object.assign(defaultSoftAsk(), c.mazam_web_push.soft_ask || {});
          push.chrome_redirect = Object.assign(
            defaultChromeRedirect(), c.mazam_web_push.chrome_redirect || {}
          );
          this.push = push;
          this.ensureLabelRules();
        }
        // Identidade só entra se o operador pedir: um modelo aplicado por
        // engano não pode trocar o nome e a foto do cliente na página.
        if (this.tplIdentidade) {
          if ('mazam_web_name' in c) this.name = c.mazam_web_name || '';
          if ('mazam_web_photo' in c) this.photo = c.mazam_web_photo || '';
        }
        useAlert('Modelo aplicado na tela. Revise e clique em Atualizar para valer nesta caixa.');
      } catch (e) {
        useAlert('Não consegui carregar o modelo.');
      }
    },
    async salvarComoModelo() {
      if (this.bloqueadoParaGerenciarModelos()) return;
      const sugestao = this.modeloPadrao ? '' : 'Segue Webchat Completo';
      const nome = window.prompt(
        'Nome do modelo (salvar com um nome já existente ATUALIZA aquele modelo):',
        sugestao
      );
      if (!nome || !nome.trim()) return;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/mazam_web/site_templates`,
          { nome: nome.trim(), inbox_id: this.inbox.id, config: this.configAtual() }
        );
        this.templates = data.templates || [];
        this.tplPadrao = data.padrao || '';
        this.tplSel = data.id || '';
        useAlert('Modelo salvo. Já dá para aplicar em outra caixa.');
      } catch (e) {
        useAlert(e.response?.data?.error || 'Não consegui salvar o modelo.');
      }
    },
    async definirPadrao() {
      if (this.bloqueadoParaGerenciarModelos()) return;
      const t = this.templates.find(x => x.id === this.tplSel);
      if (!t) return;
      if (!window.confirm(
        `Marcar "${t.nome}" como padrão? Toda CAIXA NOVA vai nascer com esta `
        + 'aparência. As caixas que já existem não mudam.'
      )) return;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/mazam_web/site_templates/padrao`,
          { id: this.tplSel, inbox_id: this.inbox.id }
        );
        this.templates = data.templates || [];
        this.tplPadrao = data.padrao || '';
        useAlert('Modelo padrão definido.');
      } catch (e) {
        useAlert(e.response?.data?.error || 'Não consegui definir o padrão.');
      }
    },
    async excluirModelo() {
      if (this.bloqueadoParaGerenciarModelos()) return;
      const t = this.templates.find(x => x.id === this.tplSel);
      if (!t) return;
      if (!window.confirm(
        `Excluir o modelo "${t.nome}"? Isso não altera a aparência das caixas já configuradas.`
      )) return;
      try {
        const { data } = await axios.delete(
          `/api/v1/accounts/${this.accountId}/mazam_web/site_templates`,
          { params: { id: this.tplSel, inbox_id: this.inbox.id } }
        );
        this.templates = data.templates || [];
        this.tplPadrao = data.padrao || '';
        this.tplSel = '';
        useAlert('Modelo excluído.');
      } catch (e) {
        useAlert(e.response?.data?.error || 'Não consegui excluir o modelo.');
      }
    },
    wppAutoPayload() {
      const [template, language] = (this.wppAutoTemplate || '').split('|');
      return {
        ativo: this.wppAutoAtivo,
        inbox_id: this.wppAutoInboxId,
        template: template || '',
        language: language || '',
      };
    },
    async save() {
      if (
        this.ehSuperAdmin &&
        this.wppAutoAtivo &&
        (!this.wppAutoInboxId || !this.wppAutoTemplate)
      ) {
        useAlert(
          'Para enviar o template ao entrar lead, escolha a caixa de WhatsApp e o template.'
        );
        return;
      }
      this.saving = true;
      const pushPayload = this.pushPayload();
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/mazam_web/config`,
          {
            inbox_id: this.inbox.id,
            name: this.name,
            photo: this.photo,
            verified: this.verified,
            whatsapp: this.whatsapp,
            meta_wpp: this.metaWpp,
            meta_wpp_inbox_id: this.metaWppInboxId,
            ...(this.ehSuperAdmin ? { wpp_auto: this.wppAutoPayload() } : {}),
            links: this.links,
            pwa: this.pwa,
            push: pushPayload,
            domain: this.cleanDomain,
            theme: this.theme,
            brand: this.brand,
            start: this.start,
          }
        );
        // O backend só liga com template aprovado e sem variáveis naquela caixa.
        if (this.ehSuperAdmin && this.wppAutoAtivo && data?.wpp_auto?.ativo !== true) {
          this.wppAutoAtivo = false;
          useAlert(
            'Configuração salva, mas o template automático NÃO foi ativado: o template não está aprovado nessa caixa ou tem variáveis.'
          );
        } else {
          useAlert('Configuração do site salva.');
        }
      } catch (e) {
        useAlert('Não foi possível salvar a configuração do site.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <form class="py-6" @submit.prevent="save">
    <div
      v-if="publicSiteUrl"
      class="mb-6 p-4 rounded-lg"
      style="background:rgba(22,163,74,.08);border:1px solid rgba(22,163,74,.35)"
    >
      <p class="text-sm font-semibold mb-2">Endereço público do seu chat</p>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <code
          style="font-family:monospace;font-size:.85rem;padding:6px 10px;border-radius:6px;background:rgba(148,163,184,.12)"
        >{{ publicSiteUrl }}</code>
        <button
          type="button"
          class="button"
          style="background:#12b886;color:#fff;border:none;padding:6px 14px;border-radius:8px;font-weight:600;cursor:pointer;font-size:.8rem"
          @click="copyPublicUrl"
        >
          {{ copied ? 'Copiado!' : 'Copiar' }}
        </button>
      </div>
      <p class="text-xs text-slate-500 mt-2">
        Use este endereço em campanhas e divulgação — é a página do seu chat
        no ar, com DNS e certificado já configurados.
      </p>
    </div>

    <!-- ===== Modelos de site =====
         Espelho, para a APARÊNCIA, do que os "Modelos de roteiro" são para o
         fluxo: salva-se a configuração visual com um nome e reaplica-se em
         quantas caixas quiser. Aplicar SÓ preenche os campos da tela — quem
         grava continua sendo o botão "Atualizar" lá embaixo.
         A2 do review 14/08: a área INTEIRA (ver, aplicar, salvar, definir
         padrão, excluir) é exclusiva do super admin id 1 — o modelo é global
         (InstallationConfig) e carrega identidade de OUTROS clientes (logo,
         nome, foto); deixar o membro da caixa só "ver e aplicar" ainda vazava
         essa identidade entre contas. v-if aqui é só a UX; o servidor trava
         de verdade (gerenciar_modelos_de_site! nas 5 ações). -->
    <div
      v-if="ehSuperAdmin"
      class="mb-6 p-4 rounded-lg"
      style="background:rgba(148,163,184,.09);border:1px solid rgba(148,163,184,.35)"
    >
      <p class="text-sm font-semibold mb-1">Modelos de site</p>
      <p class="text-xs text-slate-500 mb-3">
        Guardam a aparência da caixa (cores, logomarca, favicon, título da aba,
        textos da tela inicial, links do topo, PWA, push e selo) — nunca o
        domínio, o roteiro ou credenciais.
        <b>O modelo padrão é aplicado automaticamente em toda caixa nova.</b>
        <span v-if="modeloPadrao"> Padrão atual: <b>{{ modeloPadrao.nome }}</b>.</span>
        <span v-else> Nenhum modelo está marcado como padrão.</span>
      </p>

      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
        <select v-model="tplSel" class="!mb-0" style="min-width:230px;max-width:320px">
          <option value="">— escolher modelo —</option>
          <option v-for="t in templates" :key="t.id" :value="t.id">
            {{ t.nome }}{{ t.padrao ? ' (padrão)' : '' }}
          </option>
        </select>
        <button
          type="button"
          :disabled="!tplSel"
          style="background:#1d9bf0;color:#fff;border:none;padding:7px 14px;border-radius:8px;font-weight:600;cursor:pointer;font-size:.8rem"
          @click="usarTemplate"
        >
          Usar modelo
        </button>
        <button
          type="button"
          :disabled="!tplSel"
          style="background:transparent;border:1px solid rgba(148,163,184,.6);padding:6px 12px;border-radius:8px;font-weight:600;cursor:pointer;font-size:.8rem"
          @click="definirPadrao"
        >
          ⭐ Definir como padrão
        </button>
        <button
          type="button"
          :disabled="!tplSel"
          title="Excluir o modelo escolhido"
          style="background:transparent;border:1px solid rgba(148,163,184,.6);padding:6px 10px;border-radius:8px;cursor:pointer;font-size:.8rem"
          @click="excluirModelo"
        >
          🗑
        </button>
        <button
          type="button"
          style="background:transparent;border:1px solid rgba(148,163,184,.6);padding:6px 12px;border-radius:8px;font-weight:600;cursor:pointer;font-size:.8rem"
          @click="salvarComoModelo"
        >
          💾 Salvar como modelo
        </button>
      </div>

      <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mt-3">
        <input v-model="tplIdentidade" type="checkbox" style="margin:0" />
        <span class="text-xs">
          Aplicar também o nome e a foto exibidos no site (identidade do cliente)
        </span>
      </label>
    </div>

    <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
      Define a foto e o nome que aparecem no topo da página do cliente
      (seu domínio). Não altera o nome interno da caixa de entrada.
    </p>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Foto exibida no site</label>
      <div style="display:flex;align-items:center;gap:14px">
        <img
          v-if="previewSrc"
          :src="previewSrc"
          alt="prévia"
          style="width:56px;height:56px;border-radius:50%;object-fit:cover;flex:0 0 auto"
        />
        <div
          v-else
          style="width:56px;height:56px;border-radius:50%;background:#000;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:22px;flex:0 0 auto"
        >
          M
        </div>
        <input
          v-model="photo"
          type="text"
          style="flex:1"
          placeholder="URL da imagem (vazio = usa o avatar da caixa de entrada)"
        />
      </div>
      <div class="mt-2" style="display:flex;align-items:center;gap:8px">
        <input type="file" accept="image/png,image/jpeg" @change="e => pickImage('photo', 300, e)" />
        <span class="text-xs opacity-70">ou envie um arquivo (PNG/JPG até 300 KB) — vira a foto do topo do site</span>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Nome exibido no site</label>
      <div style="display:flex;align-items:center;gap:10px">
        <input v-model="name" type="text" class="!mb-0" style="flex:1" placeholder="Ex.: Mazam" />
        <svg v-if="verified" viewBox="0 0 22 22" style="width:22px;height:22px;flex:0 0 auto" aria-label="Selo">
          <path fill="#1d9bf0" d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.054-.878 1.687-.13.633-.08 1.29.144 1.897-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.688.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816z"/>
          <path fill="#fff" d="M9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"/>
        </svg>
      </div>
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mt-3">
        <input v-model="verified" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium">Exibir selo de verificação</span>
      </label>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">
        WhatsApp do atendimento (opcional)
      </label>
      <input
        v-model="whatsapp"
        type="text"
        placeholder="Ex.: 5511987654321 (DDI + DDD + número)"
      />
      <p class="mt-2 text-xs opacity-70">
        Preenchido, o roteiro pergunta ao cliente se ele quer continuar por aqui
        ou no WhatsApp — e o link já leva o lead identificado para este número.
        Em branco, o atendimento segue só no chat.
      </p>

      <!-- META WPP só para o super admin (id 1). O seletor lista as caixas de
           WhatsApp que o usuário enxerga, e ADMINISTRADOR enxerga as de TODOS os
           clientes (inboxes_controller#index só recorta para não-admin). Hoje o
           único administrador é o id 1, então não há vazamento — mas no dia em
           que um cliente ganhar o papel, ele veria a lista inteira, com nome e
           número das caixas dos outros. A trava fecha isso antes de acontecer.
           Mesmo padrão de criar caixa SITE WEB e gerenciar modelos de site. -->
      <label
        v-if="ehSuperAdmin"
        style="display:flex;align-items:center;gap:8px;cursor:pointer"
        class="mt-4"
      >
        <input v-model="metaWpp" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium">ATIVAR META WPP</span>
      </label>
      <p v-if="ehSuperAdmin" class="mt-1 text-xs opacity-70">
        Ligado, aparece um botão de WhatsApp dentro da conversa (ao lado do
        MODO IA). O atendente clica e continua o MESMO cliente na caixa de
        WhatsApp escolhida abaixo.
      </p>

      <div v-if="ehSuperAdmin && metaWpp" class="mt-3">
        <label class="block mb-2 text-sm font-medium">
          Caixa de WhatsApp de destino
        </label>
        <select v-model="metaWppInboxId">
          <option value="">Selecione a caixa…</option>
          <option
            v-for="caixa in caixasWhatsapp"
            :key="caixa.id"
            :value="caixa.id"
          >
            {{ caixa.name }}
          </option>
        </select>
        <p
          v-if="!caixasWhatsapp.length"
          class="mt-2 text-xs"
          style="color:#f59e0b"
        >
          Esta conta ainda não tem nenhuma caixa de WhatsApp. Crie a caixa do
          WhatsApp primeiro e volte aqui para escolher.
        </p>
        <p v-else class="mt-2 text-xs opacity-70">
          O cliente não escreveu para este número, então a janela de 24 horas da
          Meta está fechada: a conversa abre vazia e a primeira mensagem tem de
          ser um template aprovado, enviado pelo atendente na aba de Templates.
        </p>
      </div>

      <!-- Template automático no WhatsApp ao entrar lead (Kairo, 16/09/2026).
           Mesma trava do META WPP (super admin id 1): decide por QUAL caixa de
           WhatsApp sai mensagem para o cliente. Independe do META WPP acima. -->
      <label
        v-if="ehSuperAdmin"
        style="display:flex;align-items:center;gap:8px;cursor:pointer"
        class="mt-6"
      >
        <input v-model="wppAutoAtivo" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium">ENVIAR TEMPLATE NO WHATSAPP AO ENTRAR LEAD</span>
      </label>
      <p v-if="ehSuperAdmin" class="mt-1 text-xs opacity-70">
        Ligado, cada conversa nova nesta caixa dispara o template escolhido para
        o telefone do lead, pela caixa de WhatsApp abaixo. O mesmo telefone
        recebe no máximo 1 vez a cada 24 horas.
      </p>

      <div v-if="ehSuperAdmin && wppAutoAtivo" class="mt-3">
        <label class="block mb-2 text-sm font-medium">
          Caixa de WhatsApp que envia
        </label>
        <select v-model="wppAutoInboxId" @change="wppAutoTemplate = ''">
          <option value="">Selecione a caixa…</option>
          <option
            v-for="caixa in caixasWhatsapp"
            :key="`auto-${caixa.id}`"
            :value="caixa.id"
          >
            {{ caixa.name }}
          </option>
        </select>

        <template v-if="wppAutoInboxId">
          <label class="block mt-3 mb-2 text-sm font-medium">Template</label>
          <select v-model="wppAutoTemplate">
            <option value="">Selecione o template…</option>
            <option
              v-for="t in wppAutoTemplates"
              :key="`${t.name}|${t.language}`"
              :value="`${t.name}|${t.language}`"
            >
              {{ t.name }} ({{ t.language }})
            </option>
          </select>
          <p
            v-if="!wppAutoTemplates.length"
            class="mt-2 text-xs"
            style="color:#f59e0b"
          >
            Esta caixa não tem template aprovado sem variáveis. Aprove o
            template na Meta e volte aqui.
          </p>
          <p
            v-else-if="wppAutoTextoTemplate"
            class="mt-2 text-xs opacity-70"
            style="white-space:pre-line"
          >
            {{ wppAutoTextoTemplate }}
          </p>
        </template>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Domínio do site</label>
      <input v-model="domain" type="text" placeholder="Ex.: meuclt.com" />
      <div
        v-if="cleanDomain"
        class="mt-2 p-4 rounded-lg text-sm"
        style="background:rgba(29,155,240,.08);border:1px solid rgba(29,155,240,.35)"
      >
        <p class="font-semibold mb-2">📡 Apontamento no Cloudflare para {{ cleanDomain }}:</p>
        <ol class="list-decimal ml-5" style="line-height:1.9">
          <li>Registro <b>A</b> — nome <code>@</code> → conteúdo <code>{{ vpsIp }}</code></li>
          <li>Registro <b>CNAME</b> — nome <code>www</code> → conteúdo <code>{{ cleanDomain }}</code></li>
          <li>Nuvem <b>CINZA (DNS only)</b> até o site abrir com cadeado; depois pode ligar o proxy.</li>
        </ol>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Cor principal do site</label>
      <div style="display:flex;align-items:center;gap:10px">
        <input v-model="theme.brand" type="color" style="width:52px;height:36px;padding:2px" />
        <input v-model="theme.brand" type="text" class="!mb-0" style="width:130px" placeholder="#008069" />
        <span class="text-xs opacity-70">Verde WhatsApp: #008069 · Azul: #1d9bf0 · Preto: #111111</span>
      </div>
      <p class="mt-2 text-xs opacity-70">
        Vale para o botão, o anel da logo e os destaques — não muda com o tema.
      </p>
    </div>

    <!-- ===== Tema do site (fundo da tela e da conversa) ===== -->
    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Tema do site</label>
      <p class="mb-3 text-xs opacity-70">
        Muda o fundo da tela inicial e da conversa. Clique no que quiser usar.
        <b>Automático</b> acompanha o aparelho do cliente (claro de dia, escuro à noite).
      </p>
      <div
        style="display:grid;grid-template-columns:repeat(auto-fill,minmax(148px,1fr));gap:12px"
      >
        <button
          v-for="t in temas"
          :key="t.id || 'auto'"
          type="button"
          :aria-pressed="String((theme.preset || '') === t.id)"
          :style="{
            border: (theme.preset || '') === t.id ? '2px solid ' + (theme.brand || '#1d9bf0') : '1.5px solid rgba(128,128,128,.35)',
            borderRadius: '12px', padding: '0', overflow: 'hidden', cursor: 'pointer',
            background: t.painel, textAlign: 'left',
            boxShadow: (theme.preset || '') === t.id ? '0 0 0 3px ' + (theme.brand || '#1d9bf0') + '33' : 'none',
          }"
          @click="theme.preset = t.id"
        >
          <!-- prévia: barra do topo + fundo da conversa + 2 balões, nas cores reais do tema -->
          <span :style="{ display: 'block', height: '16px', background: t.painel, borderBottom: '1px solid rgba(128,128,128,.25)' }" />
          <span
            :style="{
              display: 'block', height: '62px', background: t.chat, padding: '7px 8px',
              backgroundImage: 'radial-gradient(rgba(128,128,128,.16) 1px, transparent 1.4px)',
              backgroundSize: '10px 10px',
            }"
          >
            <span :style="{ display: 'block', width: '58%', height: '13px', borderRadius: '7px', background: t.balao, marginBottom: '6px' }" />
            <span :style="{ display: 'block', width: '48%', height: '13px', borderRadius: '7px', background: t.meu, marginLeft: 'auto' }" />
          </span>
          <span :style="{ display: 'block', padding: '7px 9px 8px', background: t.painel, color: t.texto }">
            <span style="display:block;font-size:12.5px;font-weight:700;line-height:1.2">{{ t.nome }}</span>
            <span style="display:block;font-size:11px;opacity:.7;line-height:1.3">{{ t.desc }}</span>
          </span>
        </button>
      </div>

      <label class="block mt-5 mb-2 text-sm font-medium">Cor do fundo (opcional)</label>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <input v-model="theme.wallpaper" type="color" style="width:52px;height:36px;padding:2px" />
        <input v-model="theme.wallpaper" type="text" class="!mb-0" style="width:130px" placeholder="vazio = a do tema" />
        <button type="button" class="text-xs underline opacity-80" @click="theme.wallpaper = ''">
          usar a do tema
        </button>
      </div>
      <p class="mt-2 text-xs opacity-70">
        É o fundo atrás do card na tela inicial e atrás dos balões na conversa.
      </p>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Logomarca (ícones do app instalado — PNG/JPG até 300 KB)</label>
      <div style="display:flex;align-items:center;gap:12px">
        <img v-if="brand.logo" :src="brand.logo" alt="logo" style="width:44px;height:44px;border-radius:10px;object-fit:cover" />
        <input type="file" accept="image/png,image/jpeg" @change="e => pickImage('logo', 300, e)" />
        <button v-if="brand.logo" type="button" class="text-xs text-rose-400" @click="clearImage('logo')">remover</button>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Favicon (ícone da aba — PNG até 100 KB)</label>
      <div style="display:flex;align-items:center;gap:12px">
        <img v-if="brand.favicon" :src="brand.favicon" alt="favicon" style="width:24px;height:24px" />
        <input type="file" accept="image/png" @change="e => pickImage('favicon', 100, e)" />
        <button v-if="brand.favicon" type="button" class="text-xs text-rose-400" @click="clearImage('favicon')">remover</button>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Título da aba do navegador (ao lado do favicon)</label>
      <input v-model="brand.site_title" type="text" placeholder="Ex.: Meu CLT" />
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Textos da tela inicial</label>
      <p class="mb-3 text-xs opacity-70">
        Tudo que o cliente lê antes de começar a conversa. Deixe em branco para
        usar o texto padrão.
      </p>
      <input v-model="start.title" type="text" class="mb-2" placeholder='Título — ex.: "Temos uma oferta especial pré-aprovada para você." (vazio = automático)' />
      <input v-model="start.subtitle" type="text" class="mb-2" placeholder='Subtítulo (abaixo do título) — ex.: "Rápido e seguro."' />
      <input v-model="start.field_label" type="text" class="mb-2" placeholder='Rótulo do campo — padrão: "Seu número de WhatsApp"' />
      <input v-model="start.placeholder" type="text" class="mb-2" placeholder='Exemplo dentro do campo — padrão: "(11) 91234-5678"' />
      <input v-model="start.field_hint" type="text" class="mb-2" placeholder='Texto abaixo do campo — ex.: "Informe seu número e seja atendido pelos nossos especialistas."' />
      <input v-model="start.robot" type="text" class="mb-2" placeholder='Texto do "não sou um robô" — padrão: "Confirmo que não sou um robô"' />
      <input v-model="start.button" type="text" class="mb-2" placeholder='Texto do botão — ex.: "Quero ver minha oferta"' />
      <input v-model="start.consent" type="text" placeholder='Consentimento — ex.: "Ao continuar, você concorda em ser contatado neste número."' />

      <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mt-4">
        <input v-model="start.footer_on" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium">Repetir a logo no rodapé do card</span>
      </label>
    </div>

    <!-- ===== Selo do canto (prova social) ===== -->
    <div class="mb-6">
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
        <input v-model="start.badge_on" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium">Exibir selo no canto da tela inicial</span>
      </label>
      <p class="mt-2 mb-3 text-xs opacity-70">
        A plaquinha que aparece no alto do card. Serve para qualquer prova
        social — nota de reputação, tempo de mercado, número de clientes.
      </p>
      <div v-if="start.badge_on">
        <input v-model="start.badge_title" type="text" class="mb-2" placeholder='Linha de cima — ex.: "ReclameAQUI"' />
        <div style="display:flex;gap:8px;align-items:center">
          <input v-model="start.badge_value" type="text" class="!mb-0" style="flex:1" placeholder='Destaque — ex.: "9,6"' />
          <input v-model="start.badge_text" type="text" class="!mb-0" style="flex:1" placeholder='Ao lado — ex.: "Ótimo"' />
          <input
            v-model="start.badge_color"
            type="color"
            class="!mb-0"
            style="width:44px;height:38px;padding:2px;flex:0 0 auto"
            title="Cor do selo"
          />
        </div>
        <p class="mt-2 text-xs opacity-70">
          Prévia: <b>{{ start.badge_title || 'Linha de cima' }}</b> ·
          {{ start.badge_value || '0,0' }} {{ start.badge_text || 'Ótimo' }}
        </p>
      </div>
    </div>

    <!-- ===== Links no topo da página ===== -->
    <hr class="my-6" style="border-color:#e2e8f0;opacity:.4" />
    <h3 class="text-base font-semibold mb-1">Links no topo da página</h3>
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
      Ícones clicáveis exibidos no cabeçalho do chat. Marque para ativar cada um.
    </p>

    <div
      v-for="(row, key) in links"
      :key="key"
      class="mb-3"
      style="display:flex;align-items:center;gap:12px"
    >
      <label style="display:flex;align-items:center;gap:8px;min-width:110px;cursor:pointer">
        <input v-model="row.on" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium" style="text-transform:capitalize">{{ key === 'site' ? 'Site' : key === 'instagram' ? 'Instagram' : 'Facebook' }}</span>
      </label>
      <input
        v-model="row.url"
        type="text"
        style="flex:1"
        class="!mb-0"
        :placeholder="key === 'site' ? 'https://www.seusite.com.br' : key === 'instagram' ? 'https://instagram.com/perfil' : 'https://facebook.com/pagina'"
      />
    </div>

    <!-- ===== Instalação PWA ===== -->
    <hr class="my-6" style="border-color:#e2e8f0;opacity:.4" />
    <h3 class="text-base font-semibold mb-1">Instalação PWA (app na tela do celular)</h3>
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
      Mostra ao cliente uma tela convidando a instalar o app da Mazam.
      No Android a instalação é automática; no iPhone são exibidas as instruções.
    </p>

    <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mb-4">
      <input v-model="pwa.enabled" type="checkbox" style="margin:0" />
      <span class="text-sm font-medium">Exibir Instalação PWA</span>
    </label>

    <div v-if="pwa.enabled" style="padding-left:4px">
      <div class="mb-5">
        <label class="block mb-2 text-sm font-medium">Quando exibir</label>
        <select v-model="pwa.when" style="max-width:420px">
          <option value="start">Exibir ao iniciar (logo após o cliente entrar)</option>
          <option value="chat">Exibir no chat (botão vermelho "Ler Notificação!")</option>
        </select>
      </div>

      <div
        v-for="plat in ['android', 'ios']"
        :key="plat"
        class="mb-5"
        style="border:1px solid rgba(148,163,184,.35);border-radius:10px;padding:14px 16px"
      >
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mb-3">
          <input v-model="pwa[plat].on" type="checkbox" style="margin:0" />
          <span class="text-sm font-semibold">{{ plat === 'android' ? 'Android' : 'iPhone' }}</span>
          <span class="text-xs text-slate-500">{{ pwa[plat].on ? 'ativo' : 'inativo' }}</span>
        </label>
        <div class="mb-2">
          <label class="block mb-1 text-xs font-medium">Mensagem</label>
          <input
            v-model="pwa[plat].msg"
            type="text"
            class="!mb-0"
            :placeholder="`Instale o app da ${name || 'sua empresa'} e Simule seu crédito`"
          />
          <span class="text-xs text-slate-500">
            Deixe em branco para usar o nome da caixa automaticamente.
          </span>
        </div>
        <div style="display:flex;gap:10px">
          <div style="flex:1">
            <label class="block mb-1 text-xs font-medium">Botão principal</label>
            <input v-model="pwa[plat].btn_ok" type="text" class="!mb-0" />
          </div>
          <div style="flex:1">
            <label class="block mb-1 text-xs font-medium">Botão secundário</label>
            <input v-model="pwa[plat].btn_alt" type="text" class="!mb-0" />
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Notificações push ===== -->
    <hr class="my-6" style="border-color:#e2e8f0;opacity:.4" />
    <h3 class="text-base font-semibold mb-1">Notificações push (navegador)</h3>
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
      Avisa o cliente quando o atendente/IA responde, mesmo com a aba fechada.
      No iPhone só funciona se o cliente instalar o app (PWA). O aviso só aparece
      quando o cliente não está com o chat aberto.
    </p>

    <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mb-4">
      <input v-model="push.enabled" type="checkbox" style="margin:0" />
      <span class="text-sm font-medium">Ativar notificações push</span>
    </label>

    <div v-if="push.enabled" style="padding-left:4px">
      <!-- Conversão real dos últimos 30 dias -->
      <div
        v-if="stats && stats.convites_exibidos"
        class="mb-5 p-4 rounded-lg"
        style="background:rgba(148,163,184,.07)"
      >
        <label class="block mb-3 text-sm font-medium">Últimos 30 dias</label>
        <div style="display:flex;flex-wrap:wrap;gap:18px 28px">
          <div>
            <div class="text-lg font-semibold">{{ stats.convites_exibidos }}</div>
            <div class="text-xs text-slate-500">convites exibidos</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.taxa_aceite_pct }}%</div>
            <div class="text-xs text-slate-500">aceitaram o convite</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.inscricoes }}</div>
            <div class="text-xs text-slate-500">receberão avisos</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.bloqueou_no_navegador }}</div>
            <div class="text-xs text-slate-500">bloquearam no navegador</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.suprimido_pelo_navegador }}</div>
            <div class="text-xs text-slate-500">o navegador silenciou</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.recuperacoes }}</div>
            <div class="text-xs text-slate-500">reativaram depois</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.apps_instalados }}</div>
            <div class="text-xs text-slate-500">instalaram o app</div>
          </div>
        </div>
        <p class="text-xs text-slate-500 mt-3">
          Uma taxa de aceite baixa faz o Chrome esconder o pedido no sininho da barra
          de endereço. Peça no momento certo e com texto claro para manter a taxa alta.
        </p>
      </div>

      <div class="mb-5">
        <label class="block mb-2 text-sm font-medium">Quando pedir a permissão</label>
        <select v-model="push.prompt_when" style="max-width:460px">
          <option value="after_first_reply">Após a 1ª resposta do atendente/IA (recomendado)</option>
          <option value="button">Botão "Ativar notificações" no chat</option>
          <option value="on_enter">Assim que o cliente entra no chat (pior conversão)</option>
        </select>
        <p class="text-xs text-slate-500 mt-1">
          O cliente vê primeiro um convite nosso (abaixo); o pedido oficial do navegador
          só aparece se ele aceitar — assim uma recusa não bloqueia o aviso para sempre.
          Pedir antes de entregar valor derruba o aceite: prefira "após a 1ª resposta"
          ou o pedido por etiqueta, no auge do interesse. No iPhone só funciona com o
          app instalado.
        </p>
      </div>

      <div class="mb-5 p-4 rounded-lg" style="background:rgba(148,163,184,.07)">
        <label class="block mb-2 text-sm font-medium">Convite exibido ao cliente</label>
        <input
          v-model="push.soft_ask.title"
          type="text"
          class="mb-2"
          placeholder="Título — ex.: Quer ser avisado quando ficar pronto?"
        />
        <input
          v-model="push.soft_ask.text"
          type="text"
          class="mb-2"
          placeholder="Texto — ex.: Aviso você no celular assim que tiver a resposta."
        />
        <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">
          <input v-model="push.soft_ask.btn_yes" type="text" class="!mb-0" style="max-width:190px" placeholder="Botão sim" />
          <input v-model="push.soft_ask.btn_no" type="text" class="!mb-0" style="max-width:190px" placeholder="Botão não" />
          <label style="display:flex;align-items:center;gap:6px" class="text-sm">
            Reoferecer após
            <input v-model.number="push.soft_ask.snooze_days" type="number" min="1" max="60" class="!mb-0" style="width:70px" />
            dias
          </label>
        </div>
        <p class="text-xs text-slate-500 mt-2">
          Se o cliente disser "agora não", só voltamos a perguntar depois desse prazo — ou
          antes, se ele chegar numa etiqueta marcada abaixo (momento de alta intenção).
        </p>
      </div>

      <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mb-5">
        <input v-model="push.every_message" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium">Notificar em toda resposta do atendente/IA</span>
      </label>

      <div class="mb-5 p-4 rounded-lg" style="background:rgba(148,163,184,.07)">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
          <input v-model="push.chrome_redirect.on" type="checkbox" style="margin:0" />
          <span class="text-sm font-medium">Abrir no Chrome quando o navegador não suportar aviso</span>
        </label>
        <p class="text-xs text-slate-500 mt-1">
          No navegador embutido do WhatsApp/Instagram a notificação normalmente
          <b>nem existe</b>; no Samsung Internet, instalar o app dispara um alerta do
          Play Protect. Marcando aqui, o cliente do Android recebe um convite para
          continuar a conversa no Chrome (num toque, sem escolher navegador).
          Ele vai precisar informar o telefone de novo, porque é outro navegador.
          Só aparece no Android e não se repete por 7 dias.
        </p>
        <div v-if="push.chrome_redirect.on" class="mt-3">
          <input
            v-model="push.chrome_redirect.msg"
            type="text"
            class="mb-2"
            placeholder="Texto do convite"
          />
          <input
            v-model="push.chrome_redirect.btn"
            type="text"
            placeholder="Texto do botão — ex.: Abrir no Chrome"
            style="max-width:260px"
          />
        </div>
      </div>

      <label class="block mb-1 text-sm font-medium">Notificar quando estas etiquetas forem atribuídas</label>
      <p class="text-xs text-slate-500 mb-3">
        Marque a etiqueta e escreva a mensagem que o cliente recebe (ex.: oferta, link, pago).
      </p>
      <template v-for="l in allLabels" :key="l.id">
        <div
          v-if="push.labels[l.title]"
          class="mb-2"
          style="display:flex;align-items:center;gap:10px"
        >
          <label style="display:flex;align-items:center;gap:8px;min-width:190px;cursor:pointer">
            <input v-model="push.labels[l.title].on" type="checkbox" style="margin:0" />
            <span class="text-sm" style="word-break:break-all">{{ l.title }}</span>
          </label>
          <input
            v-if="push.labels[l.title].on"
            v-model="push.labels[l.title].msg"
            type="text"
            class="!mb-0"
            style="flex:1"
            placeholder="Mensagem da notificação"
          />
        </div>
      </template>
    </div>

    <hr class="my-6" style="border-color:#e2e8f0;opacity:.4" />

    <button
      type="submit"
      :disabled="saving"
      class="button"
      style="background:#12b886;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-weight:600;cursor:pointer"
    >
      {{ saving ? 'Salvando…' : 'Atualizar' }}
    </button>
  </form>
</template>
